import React from "react"
import { useTranslation } from "react-i18next"

import { ensurePrismJson } from "@/utils/prism/prism-setup"
import "prismjs/themes/prism.css"

import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded"
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material"

import { computeFoldRanges } from "./compute-fold-ranges"
import type { FoldRange } from "./compute-fold-ranges"
import { tokenizeJsonLines } from "./tokenize-json-lines"

interface JsonPreviewProps {
  isError?: boolean
  isLoading?: boolean
  isTruncated?: boolean
  previewText: string
}

const UTF8_BOM = "\uFEFF"

const stripLeadingBom = (text: string): string =>
  text.startsWith(UTF8_BOM) ? text.slice(1) : text

const tryParsePrettyJson = (previewText: string): string | null => {
  try {
    const parsed: unknown = JSON.parse(stripLeadingBom(previewText))
    return JSON.stringify(parsed, null, 2)
  } catch {
    return null
  }
}

const plainTextLines = (text: string): React.ReactNode[][] =>
  text.split("\n").map((line) => [line])

const foldRangesByStartLine = (
  foldRanges: FoldRange[]
): Map<number, FoldRange> => {
  const byStart = new Map<number, FoldRange>()
  for (const range of foldRanges) {
    byStart.set(range.startLine, range)
  }
  return byStart
}

const isLineHiddenByCollapsedFolds = (
  lineNumber: number,
  foldRanges: FoldRange[],
  collapsedStarts: ReadonlySet<number>
): boolean =>
  foldRanges.some(
    (range) =>
      collapsedStarts.has(range.startLine) &&
      lineNumber > range.startLine &&
      lineNumber < range.endLine
  )

const JsonPreviewSkeleton: React.FC = () => (
  <Stack sx={{ flex: 1, minHeight: 0 }}>
    <Skeleton sx={{ flex: 1 }} variant="rounded" width="100%" />
  </Stack>
)

const JsonPreviewRawToggle: React.FC<{
  isRawVisible: boolean
  onToggle: () => void
  previewText: string
}> = ({ isRawVisible, onToggle, previewText }) => {
  const { t } = useTranslation("common")

  return (
    <Stack sx={{ flex: 1, gap: 1, minHeight: 0 }}>
      <Button onClick={onToggle} size="small" sx={{ alignSelf: "flex-start" }}>
        {isRawVisible ? t("jsonPreview.hideRaw") : t("jsonPreview.showRaw")}
      </Button>
      {isRawVisible ? (
        <Box
          component="pre"
          sx={{
            bgcolor: "grey.50",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            flex: 1,
            fontFamily: "monospace",
            fontSize: 13,
            m: 0,
            overflow: "auto",
            p: 1.5,
            whiteSpace: "pre",
          }}
        >
          {previewText}
        </Box>
      ) : null}
    </Stack>
  )
}

const JsonPreviewFailureState: React.FC<{
  messageKey:
    | "jsonPreview.error"
    | "jsonPreview.invalid"
    | "jsonPreview.truncated"
  previewText: string | null
}> = ({ messageKey, previewText }) => {
  const { t } = useTranslation("common")
  const [isRawVisible, setIsRawVisible] = React.useState(false)

  return (
    <Stack sx={{ flex: 1, gap: 1, minHeight: 0 }}>
      <Typography color="error.main" role="status" variant="body2">
        {t(messageKey)}
      </Typography>
      {previewText !== null ? (
        <JsonPreviewRawToggle
          isRawVisible={isRawVisible}
          onToggle={() => {
            setIsRawVisible((wasVisible) => !wasVisible)
          }}
          previewText={previewText}
        />
      ) : null}
    </Stack>
  )
}

interface JsonPreviewFoldToggleProps {
  foldRange: FoldRange
  isCollapsed: boolean
  onToggle: (startLine: number) => void
}

const JsonPreviewFoldToggle: React.FC<JsonPreviewFoldToggleProps> = ({
  foldRange,
  isCollapsed,
  onToggle,
}) => {
  const { t } = useTranslation("common")
  const foldRegionId = `json-preview-fold-${foldRange.startLine}-${foldRange.endLine}`
  const labelKey = isCollapsed
    ? "jsonPreview.expandLines"
    : "jsonPreview.collapseLines"

  return (
    <IconButton
      aria-controls={foldRegionId}
      aria-expanded={!isCollapsed}
      aria-label={t(labelKey, {
        end: foldRange.endLine,
        start: foldRange.startLine,
      })}
      onClick={() => {
        onToggle(foldRange.startLine)
      }}
      size="small"
      sx={{
        height: 24,
        minHeight: 24,
        minWidth: 24,
        p: 0,
        width: 24,
      }}
    >
      {isCollapsed ? (
        <ChevronRightRoundedIcon fontSize="inherit" />
      ) : (
        <ExpandMoreRoundedIcon fontSize="inherit" />
      )}
    </IconButton>
  )
}

interface JsonPreviewLineProps {
  foldRange: FoldRange | undefined
  isCollapsed: boolean
  lineNodes: React.ReactNode[]
  lineNumber: number
  onToggleFold: (startLine: number) => void
}

const JsonPreviewLine: React.FC<JsonPreviewLineProps> = ({
  foldRange,
  isCollapsed,
  lineNodes,
  lineNumber,
  onToggleFold,
}) => (
  <Box
    sx={{
      alignItems: "flex-start",
      display: "flex",
      minHeight: "1.5em",
    }}
  >
    <Box
      aria-hidden
      component="span"
      sx={{
        borderRight: 1,
        borderRightColor: "divider",
        color: "text.secondary",
        flexShrink: 0,
        px: 1,
        textAlign: "right",
        userSelect: "none",
        width: 48,
      }}
    >
      {lineNumber}
    </Box>
    <Box
      sx={{
        flexShrink: 0,
        width: 28,
      }}
    >
      {foldRange ? (
        <JsonPreviewFoldToggle
          foldRange={foldRange}
          isCollapsed={isCollapsed}
          onToggle={onToggleFold}
        />
      ) : null}
    </Box>
    <Box
      component="span"
      id={
        foldRange
          ? `json-preview-fold-${foldRange.startLine}-${foldRange.endLine}`
          : undefined
      }
      sx={{ flex: 1, pr: 1.5 }}
    >
      {lineNodes}
      {"\n"}
    </Box>
  </Box>
)

const JsonFormattedPreview: React.FC<{
  foldRanges: FoldRange[]
  highlightedLines: React.ReactNode[][]
}> = ({ foldRanges, highlightedLines }) => {
  const [collapsedStarts, setCollapsedStarts] = React.useState(
    () => new Set<number>()
  )
  const rangesByStart = foldRangesByStartLine(foldRanges)

  const toggleFold = (startLine: number) => {
    setCollapsedStarts((previous) => {
      const next = new Set(previous)
      if (next.has(startLine)) {
        next.delete(startLine)
      } else {
        next.add(startLine)
      }
      return next
    })
  }

  const visibleLines = highlightedLines
    .map((lineNodes, index) => ({
      lineNodes,
      lineNumber: index + 1,
    }))
    .filter(
      ({ lineNumber }) =>
        !isLineHiddenByCollapsedFolds(lineNumber, foldRanges, collapsedStarts)
    )

  return (
    <Box
      className="json-preview language-json"
      component="pre"
      sx={{
        bgcolor: "grey.50",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        flex: 1,
        fontFamily: "monospace",
        fontSize: 13,
        overflow: "auto",
        whiteSpace: "pre",
        "&.json-preview": {
          m: 0,
          p: 0,
        },
        "& .token.operator": {
          background: "none",
          color: "grey.300",
        },
        "& .token.punctuation": {
          color: "grey.300",
        },
      }}
    >
      <Box
        component="code"
        sx={{
          display: "block",
          minWidth: "max-content",
        }}
      >
        {visibleLines.map(({ lineNodes, lineNumber }) => {
          const foldRange = rangesByStart.get(lineNumber)

          return (
            <JsonPreviewLine
              foldRange={foldRange}
              isCollapsed={
                foldRange !== undefined && collapsedStarts.has(lineNumber)
              }
              key={lineNumber}
              lineNodes={lineNodes}
              lineNumber={lineNumber}
              onToggleFold={toggleFold}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export const JsonPreview: React.FC<JsonPreviewProps> = ({
  isError = false,
  isLoading = false,
  isTruncated = false,
  previewText,
}) => {
  const canAttemptFormat = !isLoading && !isError && !isTruncated
  const prettyJson = canAttemptFormat ? tryParsePrettyJson(previewText) : null
  const isInvalidJson = canAttemptFormat && prettyJson === null
  const foldRanges = prettyJson !== null ? computeFoldRanges(prettyJson) : []

  const [highlightResult, setHighlightResult] = React.useState<{
    lines: React.ReactNode[][]
    prettyJson: string
  } | null>(null)

  React.useEffect(() => {
    if (prettyJson === null) {
      return
    }

    let cancelled = false

    void ensurePrismJson()
      .then((PrismApi) => {
        if (cancelled) {
          return
        }
        setHighlightResult({
          lines: tokenizeJsonLines(PrismApi, prettyJson),
          prettyJson,
        })
      })
      .catch(() => {
        if (cancelled) {
          return
        }
        // Highlighter failure is not invalid JSON — keep a readable pretty view.
        setHighlightResult({
          lines: plainTextLines(prettyJson),
          prettyJson,
        })
      })

    return () => {
      cancelled = true
    }
  }, [prettyJson])

  if (isLoading) {
    return <JsonPreviewSkeleton />
  }

  if (isError) {
    return (
      <JsonPreviewFailureState
        messageKey="jsonPreview.error"
        previewText={null}
      />
    )
  }

  if (isTruncated) {
    return (
      <JsonPreviewFailureState
        messageKey="jsonPreview.truncated"
        previewText={previewText}
      />
    )
  }

  if (isInvalidJson) {
    return (
      <JsonPreviewFailureState
        messageKey="jsonPreview.invalid"
        previewText={previewText}
      />
    )
  }

  const highlightedLines =
    highlightResult?.prettyJson === prettyJson ? highlightResult.lines : null

  if (highlightedLines === null) {
    return <JsonPreviewSkeleton />
  }

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <JsonFormattedPreview
        foldRanges={foldRanges}
        highlightedLines={highlightedLines}
      />
    </Stack>
  )
}
